import { withI18n } from '@lingui/react';
import React, { useContext, useEffect, useState } from 'react';
import { Alert } from 'reactstrap';
import { fetchMailOutboundConfig, patchMailOutboundConfig, sendTestMail } from '../../../Service/restapi/mailService';
import { LoadingAnimation } from '../../../Service/ResourceAdapter';
import { SimpleEditableText } from '../../../Atoms/inputs/SimpleEditableText';
import { StringFormatter } from '../../../Atoms/Formatter/StringFormatter';
import { EditableMailOutBoundConfigProperty } from '../../../Atoms/EditableProperties/EditableMailOutBoundConfigProperty';
import { EditableCheckbox } from '../../../Atoms/inputs/EditableCheckbox';
import { EditablePassword } from '../../../Atoms/inputs/EditablePassword';
import { PasswordFormatter } from '../../../Atoms/Formatter/PasswordFormatter';
import { TransButtonPrimaryWithSpinner } from '../../../Atoms/Buttons';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { Icon } from '../../../Atoms/Icons';
import { MailAccountProviderSelection } from './MailAccountProviderSelection';
import { ToastS } from '../../../Service/ToastS';
import { MixpanelS } from '../../../Service/MixpanelS';
import { ConstantS } from '../../../Service/ConstantS';

export const MailOutBoundConfig = withI18n()(({ i18n, id }) => {
  const { profile } = useContext(ProfileContext);
  const [success, setSuccess] = useState(false);

  const [mailOutBoundConfig, setMailOutBoundConfig] = useState(undefined);
  const [sendingTestMail, setSendingTestMail] = useState(false);

  function handleChange(patch) {
    patchMailOutboundConfig(id, patch).then((resp) => {
      if (resp.ok) {
        resp.json().then((mailOutBoundConfig) => {
          setMailOutBoundConfig(mailOutBoundConfig);
        });
      }
    });
  }

  function sendMail() {
    setSendingTestMail(true);
    sendTestMail(mailOutBoundConfig.id)
      .then((resp) => {
        if (resp.ok) {
          ToastS.success('send.mail', 'Test-E-Mail erfolgreich versendet');
          MixpanelS.track(ConstantS.TrackingEvents.MailAccountConnected);
          setSuccess(true);
        } else {
          ToastS.error('send.mail', 'Überprüfe Deine E-Mail-Konfiguration');
          console.error('could not send test mail');
          setSuccess(false);
        }
      })
      .catch((error) => {
        ToastS.error('send.mail', 'Überprüfe Deine E-Mail-Konfiguration');
        console.error('could not send test mail');
        console.error({ error });
        setSuccess(false);
      })
      .finally(() => {
        setSendingTestMail(false);
      });
  }

  useEffect(() => {
    fetchMailOutboundConfig(id).then((resp) => {
      if (resp.ok) {
        resp.json().then((mailOutBoundConfig) => {
          setMailOutBoundConfig(mailOutBoundConfig);
        });
      }
    });
  }, [id]);

  if (!mailOutBoundConfig) {
    return <LoadingAnimation />;
  }

  const styles = {
    description: {
      color: '#666666',
      marginBottom: 5,
      fontSize: 12,
    },
    smpt: {
      display: 'flex',
    },
    host: {
      flex: 2,
      marginRight: 5,
    },
    port: {
      flex: 1,
    },

    usernamePassword: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    username: {
      flex: 2,
      minWidth: 150,
      marginRight: 5,
    },
    password: {
      flex: 1,
      minWidth: 150,
    },

    replyTo: {
      color: '#9ca6af',
      fontWeight: 'unset',
    },

    authStartTls: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    auth: {
      flex: 1,
      maxWidth: 200,
      minWidth: 200,
    },
    startTls: {
      flex: 1,
      maxWidth: 150,
      minWidth: 150,
    },
    configHint: {
      marginBottom: 10,
    },
  };
  {
    /* https://myaccount.google.com/lesssecureapps */
  }
  return (
    <>
      <MailAccountProviderSelection
        mailOutBoundConfig={mailOutBoundConfig}
        changeOutBoundConfig={(patch) => {
          handleChange(patch);
          setSuccess(false);
        }}
      />

      {mailOutBoundConfig.provider === 'GMail' && (
        <div style={styles.configHint}>
          <Alert color="warning">
            <Icon value="ATTENTION" /> Für GMail ist es notwendig, dass Du für bookitup ein sog. App-spezifisches
            Passwort in Google erstellst und dieses hier verwendest. Unter{' '}
            <a
              target="_blank"
              style={{ textDecoration: 'underline' }}
              href="https://myaccount.google.com/u/1/apppasswords"
            >
              App-spezifische Passwörter
            </a>{' '}
            kannst Du dieses in Deinem Google Konto erstellen.
            <br />
            Solltest Du kein App-spezifisches Passwort erstellen können, könnte es sein, dass Du die{' '}
            <a
              target="_blank"
              style={{ textDecoration: 'underline' }}
              href="https://myaccount.google.com/u/1/signinoptions/two-step-verification"
            >
              Bestätigung in zwei Schritten
            </a>{' '}
            noch nicht aktiviert hast.
          </Alert>
        </div>
      )}
      {mailOutBoundConfig.provider === 'GMX' && (
        <div style={styles.configHint}>
          <Alert color="warning">
            <Icon value="ATTENTION" /> Für GMX ist es notwendig, dass Du für bookitup ein sog. App-spezifisches Passwort
            erstellst und dieses hier verwendest. Unter{' '}
            <a
              target="_blank"
              style={{ textDecoration: 'underline' }}
              href="https://hilfe.gmx.net/sicherheit/2fa/anwendungsspezifisches-passwort.html"
            >
              App-spezifisches Passwort erstellen
            </a>
            , findest du eine kurze Anleitung.
          </Alert>
        </div>
      )}
      {mailOutBoundConfig.provider === 'T-Online' && (
        <div style={styles.configHint}>
          <Alert color="warning">
            <Icon value="ATTENTION" /> Für T-Online ist es notwendig, dass Du ein Kennwort für E-Mail-Programme bei
            T-Online hinzufügst und dieses hier verwendest. Unter{' '}
            <a
              target="_blank"
              style={{ textDecoration: 'underline' }}
              href="https://email.t-online.de/pr?a=passwords.emailpassword"
            >
              Password für E-Mail-Programme
            </a>{' '}
            kannst Du dieses in Deinem T-Online-Konto erstellen.
          </Alert>
        </div>
      )}
      {mailOutBoundConfig.provider === 'Yahoo' && (
        <div style={styles.configHint}>
          <Alert color="warning">
            <Icon value="ATTENTION" /> Für Yahoo ist es notwendig, dass Du ein sog. App-spezifisches Passwort für
            bookitup bei Yahoo hinzufügst und dieses hier verwendest. Unter{' '}
            <a
              target="_blank"
              style={{ textDecoration: 'underline' }}
              href="https://login.yahoo.com/account/security/app-passwords/list"
            >
              App-spezifische Passwörter
            </a>{' '}
            kannst Du dieses in Deinem Yahoo-Konto erstellen. Wähle unter App auswählen 'Andere App' aus und vergib als
            Namen z.B. bookitup.
          </Alert>
        </div>
      )}
      {mailOutBoundConfig.provider === '1blu' && (
        <div style={styles.configHint}>
          <Alert color="warning">
            <Icon value="ATTENTION" /> Damit Deine 1blu Mails funktionieren muss Dein Benutzername dein (vollständiger)
            Postfachname sein - dies ist NICHT Deine E-Mail-Adresse. Den Postfachnamen findest du z.B. im{' '}
            <a target="_blank" style={{ textDecoration: 'underline' }} href="https://ksb.1blu.de">
              Kundencenter von 1blu
            </a>{' '}
            in den E-Mail-Einstellungen. In unserem Test-1blu-Konto ist dieser beispielsweise a333102_0-info
          </Alert>
        </div>
      )}

      <EditableMailOutBoundConfigProperty
        patchField="description"
        entity={mailOutBoundConfig}
        handleChange={(patch) => handleChange({ description: patch.description })}
        placeholder="Beschreibung (nur für Dich)"
        editFormatter={SimpleEditableText}
        labelFormatter={StringFormatter}
      />
      <EditableMailOutBoundConfigProperty
        patchField="email"
        entity={mailOutBoundConfig}
        handleChange={(patch) => handleChange({ email: patch.email })}
        placeholder="E-Mail-Adresse"
        editFormatter={SimpleEditableText}
        labelFormatter={StringFormatter}
      />
      <div style={styles.usernamePassword}>
        <div style={styles.username}>
          <EditableMailOutBoundConfigProperty
            patchField="login"
            entity={mailOutBoundConfig}
            handleChange={(patch) => handleChange({ login: patch.login })}
            placeholder="Benutzername"
            editFormatter={SimpleEditableText}
            labelFormatter={StringFormatter}
          />
        </div>
        <div style={styles.password}>
          <EditableMailOutBoundConfigProperty
            patchField="password"
            entity={mailOutBoundConfig}
            handleChange={(patch) => handleChange({ password: patch.password })}
            placeholder="Passwort"
            editFormatter={EditablePassword}
            labelFormatter={PasswordFormatter}
          />
        </div>
      </div>
      <div style={styles.smpt}>
        <div style={styles.host}>
          <EditableMailOutBoundConfigProperty
            patchField="smptHost"
            entity={mailOutBoundConfig}
            handleChange={(patch) => handleChange({ smptHost: patch.smptHost })}
            placeholder="SMTP-Host"
            editFormatter={SimpleEditableText}
            labelFormatter={StringFormatter}
          />
        </div>
        <div style={styles.port}>
          <EditableMailOutBoundConfigProperty
            patchField="smptPort"
            entity={mailOutBoundConfig}
            handleChange={(patch) => handleChange({ smptPort: patch.smptPort })}
            placeholder="SMTP-Port"
            editFormatter={SimpleEditableText}
            labelFormatter={StringFormatter}
          />
        </div>
      </div>
      <div style={styles.authStartTls}>
        <div style={styles.startTls}>
          <EditableMailOutBoundConfigProperty
            patchField="startTls"
            entity={mailOutBoundConfig}
            handleChange={(patch) => handleChange({ startTls: patch.startTls })}
            placeholder="StartTLS"
            editFormatter={EditableCheckbox}
            editOnly
          />
        </div>
        <div style={styles.auth}>
          <EditableMailOutBoundConfigProperty
            patchField="auth"
            entity={mailOutBoundConfig}
            handleChange={(patch) => handleChange({ auth: patch.auth })}
            placeholder="SSL-Verschlüsselt"
            editFormatter={EditableCheckbox}
            editOnly
          />
        </div>
      </div>
      <br />
      {/* <EditableMailOutBoundConfigProperty patchField="bcc" entity={mailOutBoundConfig}
                                                 handleChange={patch => handleChange({bcc: patch.bcc})}
                                                 placeholder={"Blind-Kopie-E-Mail"} editFormatter={SimpleEditableText}
                                                 labelFormatter={StringFormatter}/> */}
      {mailOutBoundConfig.provider === 'Yahoo' && (
        <div style={styles.configHint}>
          <Alert color="warning">
            <Icon value="ATTENTION" /> Yahoo unterstützt keine Antwort-an-E-Mail-Adresse. Antworten Deiner Kunden landen
            damit <b>nicht</b> automatisch in bookitup. Daher empfehlen wir hier ein anderes E-Mail-Konto zu
            hinterlegen.
          </Alert>
        </div>
      )}
      <EditableMailOutBoundConfigProperty
        labelStyle={styles.replyTo}
        patchField="replyTo"
        disabled
        entity={{ replyTo: `profile-${profile.id}@bookitup.de` }}
        handleChange={() => {}}
        placeholder="Antwort-E-Mail-Adresse"
        editFormatter={SimpleEditableText}
        editOnly
      />
      <br />
      <TransButtonPrimaryWithSpinner
        isLoading={sendingTestMail}
        icon="EMAIL_PLANE"
        onClickHandler={sendMail}
        text="Test-E-Mail verschicken"
      />
      <br />
      <br />

      {(success || mailOutBoundConfig.defaultAccount) && (
        <>
          <div style={styles.description}>
            Erfolgreich eingerichtet! Jetzt kannst Du diesen Account für Deinen E-Mail-Versand aktivieren:
          </div>

          <EditableMailOutBoundConfigProperty
            patchField="defaultAccount"
            entity={mailOutBoundConfig}
            handleChange={(patch) => handleChange({ defaultAccount: patch.defaultAccount })}
            placeholder="Diesen Account für den E-Mail-Versand verwenden"
            editFormatter={EditableCheckbox}
            editOnly
          />
        </>
      )}
    </>
  );
});
